/**
 * 身份详情页面
 * Identity Engine v9.0 + v9.1 (Facet) + v9.2 (简化版)
 * 
 * 功能：
 * - Hero 区域展示身份信息
 * - 5 个 Tab：基本信息、语料管理、场景人格、声音克隆、版本历史
 * 
 * v9.2 变更：移除"人格配置"Tab（已被"场景人格"功能覆盖）
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService, FacetApiService } from '@/modules/identity/services'
import { IdentityHero } from '@/modules/identity/components/IdentityHero'
import type { Identity, LearningStatus } from '@/modules/identity/types'

// Tab 组件
import { BasicInfoTab } from './tabs/BasicInfoTab'
import { CorpusTab } from './tabs/CorpusTab'
import { FacetTab } from './tabs/FacetTab'
import { VoiceTab } from './tabs/VoiceTab'
import { VersionTab } from './tabs/VersionTab'

// 学习进度条
import { LearningProgressBar } from '@/modules/identity/components/LearningProgressBar'

export default function IdentityDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showSuccess, showError } = useToast()

  // ==================== 状态 ====================
  const [identity, setIdentity] = useState<Identity | null>(null)
  const [learningStatus, setLearningStatus] = useState<LearningStatus | null>(null)
  const [loading, setLoading] = useState(true)
  // 从 URL 参数读取初始 Tab（支持 ?tab=facet 等）
  // v9.2: 移除 profile Tab
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    return tabParam && ['basic', 'corpus', 'facet', 'voice', 'version'].includes(tabParam)
      ? tabParam
      : 'basic'
  })
  
  // 学习进度流控制 - 不依赖 identity.status
  const [showProgressStream, setShowProgressStream] = useState(false)
  const [learningJobId, setLearningJobId] = useState<string | null>(null)
  
  // 删除确认弹窗
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // ==================== 数据加载 ====================
  const loadIdentity = useCallback(async () => {
    if (!id) return
    
    setLoading(true)
    try {
      const response = await IdentityApiService.get(id)
      setIdentity(response.data)
      
      // 如果正在学习，获取学习状态
      if (response.data.status === 'learning') {
        try {
          const statusResponse = await IdentityApiService.getLearningStatus(id)
          setLearningStatus(statusResponse.data)
        } catch {
          // 获取学习状态失败不影响主流程
          console.error('Failed to get learning status')
        }
      } else {
        setLearningStatus(null)
      }
    } catch (error) {
      console.error('Failed to load identity:', error)
      showError('无法加载身份信息，请稍后重试', '加载失败')
    } finally {
      setLoading(false)
    }
  }, [id, showError])

  useEffect(() => {
    loadIdentity()
  }, [loadIdentity])

  // 学习中时定时刷新状态
  useEffect(() => {
    if (identity?.status !== 'learning') return

    const interval = setInterval(async () => {
      if (!id) return
      try {
        const response = await IdentityApiService.get(id)
        setIdentity(response.data)
        
        if (response.data.status === 'learning') {
          const statusResponse = await IdentityApiService.getLearningStatus(id)
          setLearningStatus(statusResponse.data)
        } else {
          setLearningStatus(null)
          // 学习完成，刷新完整数据
          loadIdentity()
        }
      } catch (error) {
        console.error('Failed to refresh status:', error)
      }
    }, 3000) // 每 3 秒刷新一次

    return () => clearInterval(interval)
  }, [identity?.status, id, loadIdentity])

  // ==================== 操作处理 ====================
  const handleBack = useCallback(() => {
    navigate('/identities')
  }, [navigate])

  /**
   * v9.2: 全局学习 - 学习所有有语料的场景
   */
  const handleStartLearning = useCallback(async () => {
    if (!id) return
    try {
      // v9.2: 调用全局学习接口
      const learnResponse = await FacetApiService.learnAll(id)
      console.log('🚀 [LearnAll] 全局学习任务启动响应:', learnResponse)
      
      // 获取 job_id 用于 SSE 流
      const jobId = learnResponse.data?.job_id
      const scenesToLearn = learnResponse.data?.scenes_to_learn || []
      const scenesSkipped = learnResponse.data?.scenes_skipped || []
      console.log('🚀 [LearnAll] Job ID:', jobId)
      console.log('🚀 [LearnAll] 学习场景:', scenesToLearn.map(s => s.scene_name).join(', '))
      console.log('🚀 [LearnAll] 跳过场景:', scenesSkipped.join(', '))
      
      setLearningJobId(jobId || null)
      
      // 立即显示 SSE 进度条（不依赖 identity.status）
      setShowProgressStream(true)
      
      if (scenesToLearn.length === 0) {
        showError('没有可学习的场景，请先添加语料', '无语料')
        setShowProgressStream(false)
        return
      }
      
      showSuccess(
        `正在学习 ${scenesToLearn.length} 个场景: ${scenesToLearn.map(s => s.scene_name).join('、')}`,
        '全局学习已启动'
      )
      
      // 刷新身份数据
      loadIdentity()
      
      // 自动跳转到场景人格 Tab 查看学习进度
      setActiveTab('facet')
    } catch (error: any) {
      console.error('Failed to start learning:', error)
      // 提取后端返回的错误消息
      const errorMessage = error?.response?.data?.error?.message 
        || error?.message 
        || '无法启动学习任务'
      showError(errorMessage, '启动失败')
    }
  }, [id, showSuccess, showError, loadIdentity])

  const handleCancelLearning = useCallback(async () => {
    if (!id) return
    try {
      await IdentityApiService.cancelLearning(id)
      showSuccess('学习任务已取消', '已取消')
      loadIdentity()
    } catch (error) {
      console.error('Failed to cancel learning:', error)
      showError('无法取消学习任务', '取消失败')
    }
  }, [id, showSuccess, showError, loadIdentity])

  const handleArchive = useCallback(async () => {
    if (!id) return
    try {
      await IdentityApiService.archive(id)
      showSuccess('身份已归档', '已归档')
      loadIdentity()
    } catch (error) {
      console.error('Failed to archive:', error)
      showError('归档失败', '操作失败')
    }
  }, [id, showSuccess, showError, loadIdentity])

  const handleUnarchive = useCallback(async () => {
    if (!id) return
    try {
      await IdentityApiService.unarchive(id)
      showSuccess('身份已恢复', '已恢复')
      loadIdentity()
    } catch (error) {
      console.error('Failed to unarchive:', error)
      showError('恢复失败', '操作失败')
    }
  }, [id, showSuccess, showError, loadIdentity])

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return
    
    setDeleting(true)
    try {
      await IdentityApiService.delete(id)
      showSuccess('身份已删除', '已删除')
      navigate('/identities')
    } catch (error) {
      console.error('Failed to delete:', error)
      showError('删除失败', '操作失败')
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }, [id, showSuccess, showError, navigate])

  // ==================== 渲染 ====================
  if (loading && !identity) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-violet-600" />
          <p className="text-slate-500">加载中...</p>
        </div>
      </div>
    )
  }

  if (!identity) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500 mb-4">身份不存在或已被删除</p>
          <button
            onClick={handleBack}
            className="text-violet-600 hover:underline"
          >
            返回列表
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero 区域 */}
      <IdentityHero
        identity={identity}
        learningStatus={learningStatus}
        onBack={handleBack}
        onStartLearning={handleStartLearning}
        onCancelLearning={handleCancelLearning}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onDelete={handleDeleteClick}
        onRefresh={loadIdentity}
        isLoading={loading}
      />

      {/* SSE 学习进度条 - 独立显示，不依赖 identity.status */}
      {showProgressStream && id && (
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="bg-slate-900 rounded-lg p-4 shadow-lg">
            <LearningProgressBar
              identityId={id}
              jobId={learningJobId || undefined}
              onComplete={() => {
                setShowProgressStream(false)
                setLearningJobId(null)
                showSuccess('学习完成！请切换到「场景人格」Tab 查看结果', '完成')
                loadIdentity()
              }}
              onError={(err) => {
                setShowProgressStream(false)
                setLearningJobId(null)
                showError(err, '学习失败')
                loadIdentity()
              }}
            />
          </div>
        </div>
      )}

      {/* Tab 区域 */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="basic">基本信息</TabsTrigger>
            <TabsTrigger value="corpus">语料管理</TabsTrigger>
            <TabsTrigger value="facet">场景人格</TabsTrigger>
            <TabsTrigger value="voice">声音克隆</TabsTrigger>
            <TabsTrigger value="version">版本历史</TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="basic" className="mt-0">
              <BasicInfoTab
                identity={identity}
                onUpdate={loadIdentity}
              />
            </TabsContent>

            <TabsContent value="corpus" className="mt-0">
              <CorpusTab
                identityId={identity.id}
                onCorpusChange={loadIdentity}
              />
            </TabsContent>

            <TabsContent value="facet" className="mt-0">
              <FacetTab
                identityId={identity.id}
                onNavigateToCorpus={() => setActiveTab('corpus')}
              />
            </TabsContent>

            <TabsContent value="voice" className="mt-0">
              <VoiceTab
                identityId={identity.id}
                voiceStatus={identity.voice_status || 'none'}
                onStatusChange={loadIdentity}
              />
            </TabsContent>

            <TabsContent value="version" className="mt-0">
              <VersionTab
                identityId={identity.id}
                currentVersion={identity.version ?? 1}
              />
            </TabsContent>
          </div>
        </Tabs>
      </div>

      {/* 删除确认弹窗 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除身份「{identity.name}」吗？此操作无法撤销，所有语料和配置都将被永久删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
